import { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import SpeciesPanel from './SpeciesPanel'
import BacktestPanel from './BacktestPanel'
import {
  Activity,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  CloudRain,
  Compass,
  Droplets,
  ExternalLink,
  Gauge,
  Info,
  Leaf,
  MapPin,
  Navigation,
  RefreshCw,
  ShieldAlert,
  Sprout,
  Sun,
  ThermometerSun,
} from 'lucide-react'

const levelCopy = {
  'very low': 'veľmi nízka',
  low: 'nízka',
  moderate: 'stredná',
  high: 'vysoká',
  'very high': 'veľmi vysoká',
}

const horizonCopy = {
  0: 'Dnes',
  7: 'O 7 dní',
  14: 'O 14 dní',
}

const kindCopy = {
  forecast: 'Predpoveď',
  rain: 'Zrážky',
  observation: 'Nález',
}

const phaseCopy = {
  dry: 'stále suché podhubie',
  recharge: 'dopĺňanie vlahy',
  incubation: 'čakanie po daždi',
  'fast-fruiting': 'okno rýchlejších druhov',
  'bolete-window': 'okno hríbovitých',
  fading: 'doznievanie vlahy',
}

function formatDate(value, options = {}) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('sk-SK', {
    day: 'numeric',
    month: 'long',
    ...options,
  }).format(new Date(`${value}T12:00:00`))
}

function formatDateTime(value) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('sk-SK', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    timeZone: 'Europe/Bratislava',
  }).format(new Date(value))
}

// Bands must match chance_level() in model.py, or a card reads "vysoká" in the
// colour that means "middling".
const levelColor = {
  'very low': '#c9744f',
  low: '#df8b58',
  moderate: '#e3c85a',
  high: '#a8c94a',
  'very high': '#b5d64a',
}

function levelOf(chance) {
  if (chance < 20) return 'very low'
  if (chance < 40) return 'low'
  if (chance < 60) return 'moderate'
  if (chance < 80) return 'high'
  return 'very high'
}

function chanceColor(chance, level) {
  return levelColor[level || levelOf(chance)] || levelColor.moderate
}

// The additive model reports contributions in log-odds. Rather than print a
// unit nobody reads, bars are drawn proportional to the largest contribution of
// the day and the exact shift is stated as the score it produces.
function DriverList({ explanation, limit = 5 }) {
  const drivers = (explanation?.drivers || []).slice(0, limit)
  if (!drivers.length) return null
  const exact = explanation.exact
  const key = exact ? 'log_odds' : 'impact_percentage_points'
  const widest = Math.max(...drivers.map((driver) => Math.abs(driver[key])), 0.001)
  return (
    <div className="driver-list">
      {drivers.map((driver) => {
        const value = driver[key]
        const sign = value >= 0 ? 'positive' : 'negative'
        return (
          <div key={driver.label}>
            <span>{driver.label}</span>
            <i className={sign} style={{ width: `${Math.max(6, (Math.abs(value) / widest) * 100)}%` }} />
            <b className={sign}>
              {exact ? `${driver.share_percent} %` : `${value >= 0 ? '+' : ''}${value} p. b.`}
            </b>
          </div>
        )
      })}
    </div>
  )
}

function decompositionNote(explanation) {
  const parts = explanation?.decomposition
  if (!explanation?.exact || !parts) {
    return 'Lokálne protifaktové vplyvy sa pre interakcie nesčítavajú.'
  }
  const typical = Math.round(parts.typical_day_score * 100)
  const today = Math.round(parts.this_day_score * 100)
  return `Model je aditívny, takže tieto podiely tvoria presne celý rozdiel: z typického dňa (${typical}) na ${today} v surovom skóre modelu. Percentá sú podiely na celkovom posune, nie pravdepodobnosť nálezu.`
}

const logit = (p) => Math.log(p / (1 - p))
const sigmoid = (v) => 1 / (1 + Math.exp(-v))

// The additive decomposition is exact, and a waterfall is the one chart that
// shows exactly that: start at a typical day, add every group's push, land on
// this day's score. Bars live in log-odds (the model's own unit); the two end
// labels translate the ends back into the raw score a reader recognises.
function Waterfall({ explanation, compact = false }) {
  const parts = explanation?.decomposition
  const drivers = explanation?.drivers || []
  if (!explanation?.exact || !parts || !drivers.length) return null
  const start = logit(parts.typical_day_score)
  const steps = []
  let running = start
  drivers.forEach((driver) => {
    steps.push({ label: driver.label, from: running, to: running + driver.log_odds, value: driver.log_odds })
    running += driver.log_odds
  })
  const end = running
  const width = 520
  const height = compact ? 132 : 150
  const left = 8
  const right = width - 8
  const top = 30
  const bottom = height - 32
  const levels = [start, end, ...steps.flatMap((step) => [step.from, step.to])]
  const low = Math.min(...levels)
  const high = Math.max(...levels)
  const span = Math.max(high - low, 0.5)
  const y = (value) => bottom - ((value - low) / span) * (bottom - top)
  const columns = steps.length + 2
  const slot = (right - left) / columns
  const barWidth = Math.min(slot * 0.62, 54)
  const xOf = (index) => left + slot * index + (slot - barWidth) / 2
  const shortLabels = {
    'Sezóna': 'sezóna',
    'Vlhkosť, výpar a tlak': 'vlhkosť a výpar',
    'Nočné a denné teploty': 'teploty',
    'Dážď a pôdna vlaha': 'dážď a vlaha',
    'Čerstvá aktivita hubárov': 'hlásenia',
    'Čo hlásili hubári naposledy': 'hlásenia',
  }
  const short = (text) => shortLabels[text] || (text.length > 16 ? `${text.slice(0, 15)}…` : text)

  return (
    <svg className="waterfall" viewBox={`0 0 ${width} ${height}`} role="img"
      aria-label={`Vodopád: z typického dňa ${Math.round(parts.typical_day_score * 100)} na ${Math.round(parts.this_day_score * 100)} v surovom skóre`}>
      <line className="waterfall-base" x1={left} x2={right} y1={y(start)} y2={y(start)} />
      <g className="waterfall-end">
        <rect x={xOf(0)} y={y(start)} width={barWidth} height={Math.max(2, bottom - y(start))} rx="3" />
        <text x={xOf(0) + barWidth / 2} y={y(start) - 6} textAnchor="middle">{Math.round(parts.typical_day_score * 100)}</text>
        <text className="waterfall-label" x={xOf(0) + barWidth / 2} y={bottom + 14} textAnchor="middle">typický deň</text>
      </g>
      {steps.map((step, index) => {
        const positive = step.value >= 0
        const topY = y(Math.max(step.from, step.to))
        const barHeight = Math.max(2, Math.abs(y(step.from) - y(step.to)))
        const x = xOf(index + 1)
        return (
          <g key={step.label} className={positive ? 'waterfall-up' : 'waterfall-down'}>
            <line className="waterfall-link" x1={x - (slot - barWidth) / 2} x2={x} y1={y(step.from)} y2={y(step.from)} />
            <rect x={x} y={topY} width={barWidth} height={barHeight} rx="3" />
            <text x={x + barWidth / 2} y={topY - 6} textAnchor="middle">
              {positive ? '+' : '−'}{Math.abs(step.value).toFixed(2)}
            </text>
            <text className="waterfall-label" x={x + barWidth / 2} y={bottom + 14} textAnchor="middle">
              <title>{step.label}</title>{short(step.label)}
            </text>
          </g>
        )
      })}
      <g className="waterfall-end waterfall-end--today">
        <line className="waterfall-link" x1={xOf(columns - 1) - (slot - barWidth) / 2} x2={xOf(columns - 1)} y1={y(end)} y2={y(end)} />
        <rect x={xOf(columns - 1)} y={y(end)} width={barWidth} height={Math.max(2, bottom - y(end))} rx="3" />
        <text x={xOf(columns - 1) + barWidth / 2} y={y(end) - 6} textAnchor="middle">{Math.round(sigmoid(end) * 100)}</text>
        <text className="waterfall-label" x={xOf(columns - 1) + barWidth / 2} y={bottom + 14} textAnchor="middle">tento deň</text>
      </g>
      <text className="waterfall-unit" x={right} y={top - 16} textAnchor="end">stĺpce: log-šance · konce: surové skóre modelu</text>
    </svg>
  )
}

function moveChartSelection(event, items, currentIndex, select) {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
  event.preventDefault()
  let next = currentIndex
  if (event.key === 'ArrowLeft') next = Math.max(0, currentIndex - 1)
  if (event.key === 'ArrowRight') next = Math.min(items.length - 1, currentIndex + 1)
  if (event.key === 'Home') next = 0
  if (event.key === 'End') next = items.length - 1
  select(items[next].date)
}

function HeroCurve({ points, asOf }) {
  const [selectedDate, setSelectedDate] = useState(asOf)
  if (!points?.length) return null
  const todayIndex = Math.max(points.findIndex((point) => point.date === asOf), 0)
  const selected = points.find((point) => point.date === selectedDate) || points[todayIndex]
  const selectedIndex = points.indexOf(selected)
  const futurePoints = points.filter((point) => point.day_offset >= 0)
  const peak = futurePoints.reduce((best, point) => point.chance_percent > best.chance_percent ? point : best)
  const daySeven = points.find((point) => point.day_offset === 7)
  const dayFourteen = points.find((point) => point.day_offset === 14)
  const left = 42
  const right = 508
  const top = 38
  const bottom = 226
  const x = (index) => left + (index / Math.max(points.length - 1, 1)) * (right - left)
  const y = (chance) => bottom - (chance / 100) * (bottom - top)
  const line = (items, offset = 0) => items.map((point, index) => `${x(index + offset)},${y(point.chance_percent)}`).join(' ')
  const selectFromPointer = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    const raw = (event.clientX - bounds.left) / bounds.width
    const ratio = Math.max(0, Math.min(1, (raw - left / 550) / ((right - left) / 550)))
    setSelectedDate(points[Math.round(ratio * (points.length - 1))].date)
  }
  return (
    <div className="hero-curve" aria-label="Krivka indexu vhodnosti 14 dní dozadu a dopredu">
      <div className="hero-curve-head">
        <div><span>{selected.forecast ? 'Predpoveď' : selected.reconstruction ? 'Rekonštrukcia' : 'Dnes'}</span><strong>{formatDate(selected.date, { weekday: 'short' })}</strong></div>
        <b>{selected.chance_percent}<small>/100</small></b>
        <p><CloudRain size={13} /> {selected.weather.rain_14d_mm} mm <ThermometerSun size={13} /> noc {selected.weather.night_temperature_c} °C</p>
      </div>
      <svg viewBox="0 0 550 270" role="slider" tabIndex="0"
        aria-label="Historický sezónny percentil vhodnosti; šípkami vyber deň"
        aria-valuemin="0" aria-valuemax={points.length - 1} aria-valuenow={selectedIndex}
        aria-valuetext={`${formatDate(selected.date)}: ${selected.chance_percent} zo 100`}
        onKeyDown={(event) => moveChartSelection(event, points, selectedIndex, setSelectedDate)}
        onPointerMove={(event) => { if (event.buttons === 1) selectFromPointer(event) }} onClick={selectFromPointer}>
        <rect className="hero-future-zone" x={x(todayIndex)} y={top} width={right - x(todayIndex)} height={bottom - top} />
        {[25, 50, 75, 100].map((value) => (
          <g className="hero-curve-grid" key={value}><line x1={left} x2={right} y1={y(value)} y2={y(value)} /><text x="5" y={y(value) + 3}>{value}</text></g>
        ))}
        <polygon className="hero-area-past" points={`${x(0)},${bottom} ${line(points.slice(0, todayIndex + 1))} ${x(todayIndex)},${bottom}`} />
        <polygon className="hero-area-future" points={`${x(todayIndex)},${bottom} ${line(points.slice(todayIndex), todayIndex)} ${right},${bottom}`} />
        <polyline className="hero-curve-past" points={line(points.slice(0, todayIndex + 1))} />
        <polyline className="hero-curve-future" points={line(points.slice(todayIndex), todayIndex)} />
        {points.map((point, index) => <circle className={index === selectedIndex ? 'selected' : ''} key={point.date} cx={x(index)} cy={y(point.chance_percent)} r={index === selectedIndex ? 5 : index === todayIndex ? 4 : 2.2} />)}
        <line className="hero-today-line" x1={x(todayIndex)} x2={x(todayIndex)} y1={top - 8} y2={bottom + 8} />
        <line className="hero-selected-line" x1={x(selectedIndex)} x2={x(selectedIndex)} y1={top} y2={bottom} />
        <text x={left} y="252">−14 DNÍ</text><text x={x(todayIndex)} y="252" textAnchor="middle">DNES</text><text x={right} y="252" textAnchor="end">+14 DNÍ</text>
        <text className="hero-zone-label" x={x(todayIndex) - 10} y="24" textAnchor="end">MINULOSŤ</text>
        <text className="hero-zone-label future" x={x(todayIndex) + 10} y="24">PREDPOVEĎ</text>
      </svg>
      <div className="hero-curve-stats">
        <div><span>Dnes</span><strong>{points[todayIndex].chance_percent}/100</strong></div>
        <div><span>O 7 dní</span><strong>{daySeven?.chance_percent}/100</strong></div>
        <div><span>O 14 dní</span><strong>{dayFourteen?.chance_percent}/100</strong></div>
        <div className="peak"><span>Vrchol výhľadu</span><strong>{peak.chance_percent}/100 · {formatDate(peak.date)}</strong></div>
      </div>
      {selected.explanation && (
        <div className="hero-why">
          <div className="hero-why-copy">
            <span>Prečo práve {selected.chance_percent}/100?</span>
            <p>{selected.explanation.facts.slice(0, 6).join(' · ')}</p>
          </div>
          <DriverList explanation={selected.explanation} />
          <Waterfall explanation={selected.explanation} compact />
          <small>{decompositionNote(selected.explanation)}</small>
        </div>
      )}
    </div>
  )
}

function ForecastCard({ item, current = false, curve }) {
  const color = chanceColor(item.chance_percent, item.level)
  if (current) {
    const fieldChance = item.chance_percent
    const soil = soilState(item)
    const cautious = fieldChance < 60 || soil.dry
    // The outlook is on the same scale as today, so a rising fortnight is a
    // real statement and the headline should not contradict the chart beside it.
    const ahead = (curve || []).filter((point) => point.day_offset > 0)
    const best = ahead.length
      ? ahead.reduce((top, point) => (point.chance_percent > top.chance_percent ? point : top))
      : null
    const improving = best && best.chance_percent - fieldChance >= 12
    return (
      <article className="current-card">
        <div className="current-card__copy">
          <span className="eyebrow"><Sprout size={15} /> Podmienky v regióne</span>
          <h1>
            {soil.dry && fieldChance < 80 ? <>Pôda je suchá,<br />dnes ešte nie.</>
              : cautious && improving ? <>Dnes ešte nie,<br />ale zlepšuje sa to.</>
                : cautious ? <>Dnes by som ešte<br />chvíľu počkal.</>
                  : <>Dnes sa oplatí<br />pozrieť do lesa.</>}
          </h1>
          <p>
            Index {fieldChance}/100 je percentil voči historickým dňom okolo tejto
            časti sezóny, ktoré model nikdy nevidel pri učení. Rovnaká škála platí
            pre dnešok aj pre celý výhľad, takže sa dajú porovnávať.
            {soil.dry && ` ${soil.note}`}
            {improving && ` O ${best.day_offset} dní model počíta ${best.chance_percent}/100.`}
          </p>
          <RecentReports reports={item.recent_reports} />
          <div className="current-meta">
            <span><CalendarDays size={17} /> {formatDate(item.date, { weekday: 'long' })}</span>
            <span className="status-dot"><i /> Aktualizované</span>
          </div>
        </div>
        <HeroCurve points={curve} asOf={item.date} />
      </article>
    )
  }

  return (
    <article className="forecast-card">
      <div className="forecast-card__top">
        <div>
          <span>{horizonCopy[item.horizon_days]}</span>
          <h3>{formatDate(item.date, { weekday: 'short' })}</h3>
        </div>
        <strong style={{ color }}>{item.chance_percent}<small>/100</small></strong>
      </div>
      <div className="meter"><i style={{ width: `${item.chance_percent}%`, background: color }} /></div>
      <div className="forecast-card__bottom">
        <span>{levelCopy[item.level] || item.level} vhodnosť</span>
        <span>{item.weather.rain_14d_mm} mm / 14 dní</span>
      </div>
    </article>
  )
}

function WeatherStrip({ item }) {
  const values = [
    { icon: CloudRain, label: 'Zrážky / 14 dní', value: `${item.weather.rain_14d_mm} mm` },
    { icon: ThermometerSun, label: 'Teplota', value: `${item.weather.temperature_c} °C` },
    { icon: Droplets, label: 'Vlhkosť', value: `${item.weather.humidity_percent} %` },
    { icon: Gauge, label: 'Tlak vzduchu', value: `${item.weather.pressure_hpa} hPa` },
  ]
  return (
    <section className="weather-strip" aria-label="Dnešné počasie">
      {values.map(({ icon: Icon, label, value }) => (
        <div className="weather-value" key={label}>
          <Icon size={20} />
          <div><span>{label}</span><strong>{value}</strong></div>
        </div>
      ))}
    </section>
  )
}

// The index is a rank against the same part of past seasons, so a middling
// rank in a dry spell can still be a hopeless day. The soil store is the
// absolute check: an empty sand reservoir vetoes "go" whatever the rank says.
function soilState(item) {
  const store = item?.weather?.sand_moisture_index
  const balance = item?.weather?.water_balance_14d_mm
  if (!Number.isFinite(store)) return { dry: false, note: '' }
  const dry = store <= 0.5
  const note = dry
    ? `Zásoba vody v piesku je ${store.toLocaleString('sk-SK')} mm${Number.isFinite(balance) ? ` a 14-dňová bilancia ${balance.toLocaleString('sk-SK')} mm` : ''}: dážď padol, ale pôda ho nedržala.`
    : ''
  return { dry, store, balance, note }
}

function decisionVerdict(chance, soil = { dry: false }) {
  if (soil.dry && chance < 80) {
    return { label: 'Dnes nie', tone: 'wait', detail: 'Zásoba vody v piesčitej pôde je vyčerpaná; poradie dňa je slušné, ale bez vlahy plodnice nevyrastú.' }
  }
  if (chance >= 80) return { label: 'Choď', tone: 'go', detail: 'Veľmi dobrý deň na výjazd.' }
  if (chance >= 60) return { label: 'Choď', tone: 'go', detail: 'Podmienky stoja za cestu.' }
  if (chance >= 40) return { label: 'Skús to', tone: 'maybe', detail: 'Má zmysel krátky výjazd nablízku, bez veľkých očakávaní.' }
  return { label: 'Dnes nie', tone: 'wait', detail: 'Na samostatný výjazd sú podmienky slabé.' }
}

// The last fortnight of community reports, split by what people actually
// found. Shown next to the verdict because a run of "nothing grew" is the most
// direct evidence there is, and it now also enters the model.
function RecentReports({ reports }) {
  if (!reports) return null
  const total = reports.grew_14d + reports.no_grew_14d
  if (!total) return <p className="recent-reports is-empty"><Leaf size={14} /> Za posledných 14 dní nikto z Malaciek nehlásil.</p>
  const share = Math.round((reports.grew_14d / total) * 100)
  return (
    <div className={`recent-reports ${share >= 50 ? 'is-good' : 'is-bad'}`}>
      <Leaf size={14} />
      <span>
        Hlásenia z lesa za 14 dní: <b>{reports.grew_14d}× rástli</b>, <b>{reports.no_grew_14d}× nerástli</b>
        {reports.days_since_growth_report < 60 && ` · posledné „rástli“ pred ${reports.days_since_growth_report} dňami`}
      </span>
      <i className="recent-reports-bar" aria-hidden="true"><em style={{ width: `${share}%` }} /></i>
    </div>
  )
}

function DecisionSummary({ current, points, guide }) {
  const verdict = decisionVerdict(current.chance_percent, soilState(current))
  const todayRating = Math.max(1, Math.round(current.chance_percent / 10))
  const future = (points || []).filter((point) => point.day_offset > 0)
  const best = future.length
    ? future.reduce((top, point) => (point.chance_percent > top.chance_percent ? point : top))
    : null
  const bestVerdict = best ? decisionVerdict(best.chance_percent, soilState(best)) : null
  const location = guide?.recommendations?.find((item) => item.parking_verified)
    || guide?.recommendations?.[0]
  const links = browserMapLinks(location)
  const access = location?.access_windows?.join(' + ')

  return (
    <section className={`decision-summary decision-summary--${verdict.tone}`} aria-labelledby="decision-title">
      <div className="decision-verdict">
        <span className="eyebrow"><Compass size={15} /> Rozhodnutie na dnes</span>
        <div className="decision-score"><strong>{todayRating}/10</strong><span id="decision-title">{verdict.label}</span></div>
        <p>{verdict.detail}</p>
        {best && (
          <small>
            Najlepší deň výhľadu: <b>{formatDate(best.date, { weekday: 'long' })}</b>, {Math.max(1, Math.round(best.chance_percent / 10))}/10
            {bestVerdict?.label === 'Choď' ? ' — naplánuj si ho.' : ' — ani výhľad zatiaľ nie je silný.'}
          </small>
        )}
      </div>
      <div className="decision-place">
        <span className="eyebrow"><MapPin size={15} /> Kam ísť</span>
        {location ? (
          <>
            <h2>{location.name}</h2>
            <p>{location.habitat} · hľadaj najmä {(location.species || []).slice(0, 3).map((item) => item.name).join(', ')}</p>
            <div className="decision-facts">
              <span><Sun size={15} /> iba za svetla {access || `${location.sunrise}–${location.sunset}`}</span>
              <span><Navigation size={15} /> {location.parking_verified ? 'overené parkovanie' : 'terénny nástup'} · pešo približne {location.walk_to_search_zone_km} km</span>
            </div>
            <div className="decision-actions">
              <a className="primary" href={links.google_directions_url} target="_blank" rel="noreferrer"><Navigation size={13} /> Navigovať na štart</a>
              <a href="#mapa">Pozrieť ďalšie miesta <ArrowRight size={13} /></a>
            </div>
            <small>Miesta sú expertné poradie podmienok, nie lokálna pravdepodobnosť nálezu.</small>
          </>
        ) : <p>Lokalitu sa nepodarilo pripraviť; over mapu a podmienky ručne.</p>}
      </div>
    </section>
  )
}

function PredictionTimeline({ points, asOf }) {
  const [selectedDate, setSelectedDate] = useState(asOf)
  if (!points?.length) return null
  const selected = points.find((item) => item.date === selectedDate)
    || points.find((item) => item.date === asOf)
    || points.at(-1)
  const todayIndex = Math.max(points.findIndex((item) => item.date === asOf), 0)
  const left = 46
  const right = 954
  const top = 30
  const bottom = 215
  // Two aligned bands under the index: daily rain and night minimum. They are
  // the two inputs a forager can check against their own memory, and drawing
  // them on the same date axis makes the rain-to-index delay visible.
  const rainTop = 262
  const rainBottom = 322
  const nightTop = 348
  const nightBottom = 396
  const x = (index) => left + (index / Math.max(points.length - 1, 1)) * (right - left)
  const y = (chance) => bottom - (chance / 100) * (bottom - top)
  const rains = points.map((item) => item.weather?.rain_mm ?? 0)
  const maxRain = Math.max(...rains, 5)
  const rainY = (mm) => rainBottom - (mm / maxRain) * (rainBottom - rainTop)
  const nights = points.map((item) => item.weather?.night_temperature_c).filter(Number.isFinite)
  const nightLow = Math.min(...nights, 4)
  const nightHigh = Math.max(...nights, 20)
  const nightY = (celsius) => nightBottom - ((celsius - nightLow) / Math.max(nightHigh - nightLow, 1)) * (nightBottom - nightTop)
  const nightBand = { from: Math.max(8, nightLow), to: Math.min(16, nightHigh) }
  const slot = (right - left) / Math.max(points.length - 1, 1)
  const curve = (items, offset = 0) => items.map((item, index) => (
    `${x(index + offset).toFixed(1)},${y(item.chance_percent).toFixed(1)}`
  )).join(' ')
  const nightLine = (items) => items
    .filter((item) => Number.isFinite(item.weather?.night_temperature_c))
    .map((item) => `${x(points.indexOf(item)).toFixed(1)},${nightY(item.weather.night_temperature_c).toFixed(1)}`)
    .join(' ') || undefined
  const past = points.slice(0, todayIndex + 1)
  const future = points.slice(todayIndex)
  const selectFromPointer = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    const raw = (event.clientX - bounds.left) / bounds.width
    const ratio = Math.max(0, Math.min(1, (raw - left / 1000) / ((right - left) / 1000)))
    setSelectedDate(points[Math.round(ratio * (points.length - 1))].date)
  }
  const selectedIndex = points.indexOf(selected)

  return (
    <section className="prediction-timeline" id="denna-predpoved">
      <div className="section-heading prediction-heading">
        <div>
          <span className="eyebrow"><Sprout size={15} /> D−14 až D+14</span>
          <h2>Denná krivka a čo ju tvorí</h2>
        </div>
        <div className="curve-key"><span><i /> rekonštrukcia</span><span><i className="future" /> predpoveď</span><span><i className="rain" /> dážď</span><span><i className="night" /> nočné minimum</span></div>
      </div>
      <div className="prediction-readout" aria-live="polite">
        <div><span>{selected.forecast ? 'Predpoveď' : selected.reconstruction ? 'Rekonštrukcia' : 'Dnes'}</span><strong>{formatDate(selected.date, { weekday: 'long', year: 'numeric' })}</strong></div>
        <b style={{ color: chanceColor(selected.chance_percent, selected.level) }}>{selected.chance_percent}<small>/100</small></b>
        <span><CloudRain size={14} /> {selected.weather.rain_mm} mm · {selected.weather.rain_14d_mm} mm / 14 dní</span>
        <span><ThermometerSun size={14} /> noc {selected.weather.night_temperature_c} °C</span>
        <span><Gauge size={14} /> {selected.weather.pressure_hpa} hPa</span>
      </div>
      <div className="prediction-chart-scroll">
        <svg className="prediction-chart" viewBox="0 0 1000 420" role="slider" tabIndex="0"
          aria-label="Denný index vhodnosti od 14 dní dozadu po 14 dní dopredu, s denným dažďom a nočným minimom; šípkami vyber deň"
          aria-valuemin="0" aria-valuemax={points.length - 1} aria-valuenow={selectedIndex}
          aria-valuetext={`${formatDate(selected.date)}: ${selected.chance_percent} zo 100, ${selected.weather.rain_mm} mm, noc ${selected.weather.night_temperature_c} °C`}
          onKeyDown={(event) => moveChartSelection(event, points, selectedIndex, setSelectedDate)}
          onPointerMove={(event) => { if (event.buttons === 1) selectFromPointer(event) }} onClick={selectFromPointer}>
          <rect className="future-zone" x={x(todayIndex)} y="18" width={right - x(todayIndex)} height={nightBottom - 18 + 8} />
          <g className="probability-grid">
            {[25, 50, 75, 100].map((value) => (
              <g key={value}><line x1={left} x2={right} y1={y(value)} y2={y(value)} /><text x="8" y={y(value) + 3}>{value}</text></g>
            ))}
          </g>
          <text className="band-title" x={left} y={top - 12}>INDEX VHODNOSTI · 0–100</text>
          <polyline className="probability-line probability-line--past" points={curve(past)} />
          <polyline className="probability-line probability-line--future" points={curve(future, todayIndex)} />
          {points.map((item, index) => (
            <g key={item.date}>
              <circle className={`probability-point ${item.forecast ? 'is-future' : ''}`} cx={x(index)} cy={y(item.chance_percent)} r={item.date === selected.date ? 5.5 : 3} />
              {item.observed_report && <circle className="report-marker" cx={x(index)} cy="232" r="2.7"><title>V tento deň bolo komunitné hlásenie</title></circle>}
            </g>
          ))}

          <g className="rain-band">
            <text className="band-title" x={left} y={rainTop - 8}>DENNÝ DÁŽĎ · mm (najvyšší {maxRain.toFixed(0)} mm)</text>
            <line className="band-base" x1={left} x2={right} y1={rainBottom} y2={rainBottom} />
            {points.map((item, index) => {
              const mm = item.weather?.rain_mm ?? 0
              const heavy = mm >= 10
              return (
                <rect key={item.date} className={`rain-bar ${item.forecast ? 'is-future' : ''} ${heavy ? 'is-heavy' : ''}`}
                  x={x(index) - slot * 0.32} y={rainY(mm)} width={slot * 0.64} height={Math.max(0.8, rainBottom - rainY(mm))} rx="1.5">
                  <title>{formatDate(item.date)}: {mm} mm{heavy ? ' · výdatný dážď' : ''}</title>
                </rect>
              )
            })}
          </g>

          <g className="night-band">
            <text className="band-title" x={left} y={nightTop - 8}>NOČNÉ MINIMUM · °C (pás 8–16 °C je pre plodnice najlepší)</text>
            {nightBand.to > nightBand.from && (
              <rect className="night-optimal" x={left} y={nightY(nightBand.to)} width={right - left} height={nightY(nightBand.from) - nightY(nightBand.to)} />
            )}
            <line className="band-base" x1={left} x2={right} y1={nightBottom} y2={nightBottom} />
            <text className="band-axis" x={left - 6} y={nightY(nightBand.from) + 3} textAnchor="end">{nightBand.from}°</text>
            <text className="band-axis" x={left - 6} y={nightY(nightBand.to) + 3} textAnchor="end">{nightBand.to}°</text>
            <polyline className="night-line night-line--past" points={nightLine(past)} />
            <polyline className="night-line night-line--future" points={nightLine(future)} />
            {points.map((item, index) => Number.isFinite(item.weather?.night_temperature_c) && (
              <circle key={item.date} className="night-point" cx={x(index)} cy={nightY(item.weather.night_temperature_c)} r={item.date === selected.date ? 4 : 2.2}>
                <title>{formatDate(item.date)}: noc {item.weather.night_temperature_c} °C</title>
              </circle>
            ))}
          </g>

          <line className="today-line" x1={x(todayIndex)} x2={x(todayIndex)} y1="15" y2={nightBottom + 8} />
          <text className="today-label" x={x(todayIndex)} y={nightBottom + 22} textAnchor="middle">DNES</text>
          <line className="curve-selected-line" x1={x(selectedIndex)} x2={x(selectedIndex)} y1="18" y2={nightBottom} />
        </svg>
        <div className="prediction-dates"><time>{formatDate(points[0].date)}</time><span>bod pod krivkou = komunitné hlásenie · dážď sa v indexe prejaví s oneskorením dní až dvoch týždňov</span><time>{formatDate(points.at(-1).date)}</time></div>
      </div>
      <p className="curve-note">Minulých 14 dní je rekonštrukcia dnešným modelom, nie archivovaná predpoveď z daného dňa. Budúcich 14 dní používa dennú predpoveď počasia; nejde o interpoláciu kariet Dnes / +7 / +14. Dážď a nočné minimá sú tie isté hodnoty, ktoré model dostáva na vstup.</p>
    </section>
  )
}

const seriesRanges = [
  [3, '3 dni'], [7, '7 dní'], [14, '14 dní'],
  [30, '30 dní'], [90, '90 dní'], [365, '1 rok'],
  [730, '2 roky'], [1095, '3 roky'], [1830, '5 rokov'],
]

function WeatherTimeSeries({ asOf }) {
  const [range, setRange] = useState(30)
  const [selectedDate, setSelectedDate] = useState(null)
  const [series, setSeries] = useState(null)
  const [loaded, setLoaded] = useState(0)

  // Five years of daily weather is far too big to block the first paint on.
  // Fetch only the span the picker is showing, and widen it on demand.
  useEffect(() => {
    if (range <= loaded) return
    let cancelled = false
    fetch(`/api/weather-series?days=${range}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (cancelled || !payload) return
        setSeries(payload)
        setLoaded(range)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [range, loaded])

  const history = (series?.daily || []).filter((item) => item.date <= asOf)
  const visible = history.slice(-range)
  const selected = visible.find((item) => item.date === selectedDate) || visible.at(-1)
  if (!visible.length) {
    return (
      <section className="series-panel is-loading-series" id="historia" aria-busy="true">
        <div className="section-heading series-heading">
          <div>
            <span className="eyebrow"><Activity size={15} /> Denná časová rada</span>
            <h2>Dážď, teplota a tlak v čase</h2>
          </div>
        </div>
        <p className="series-placeholder">Načítavam meranú históriu počasia…</p>
      </section>
    )
  }

  const width = 1000
  const x = (index) => (index / Math.max(visible.length - 1, 1)) * width
  const extent = (key, padding = 0) => {
    const values = visible.map((item) => item[key]).filter(Number.isFinite)
    return [Math.min(...values) - padding, Math.max(...values) + padding]
  }
  const line = (key, top, bottom, domain) => {
    const [minimum, maximum] = domain
    const span = Math.max(maximum - minimum, 1)
    return visible.map((item, index) => {
      const value = item[key]
      if (!Number.isFinite(value)) return null
      const y = bottom - ((value - minimum) / span) * (bottom - top)
      return `${x(index).toFixed(2)},${y.toFixed(2)}`
    }).filter(Boolean).join(' ')
  }
  const temperatureDomain = extent('temperature_min_c', 2)
  temperatureDomain[1] = Math.max(
    ...visible.map((item) => item.temperature_max_c).filter(Number.isFinite),
    temperatureDomain[1],
  ) + 2
  const pressureDomain = extent('pressure_hpa', 2)
  const maxRain = Math.max(...visible.map((item) => item.rain_mm || 0), 1)
  const selectedIndex = Math.max(visible.indexOf(selected), 0)
  const selectFromPointer = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width))
    setSelectedDate(visible[Math.round(ratio * (visible.length - 1))].date)
  }

  return (
    <section className="series-panel" id="historia">
      <div className="section-heading series-heading">
        <div>
          <span className="eyebrow"><Activity size={15} /> Denná časová rada</span>
          <h2>Dážď, teplota a tlak v čase</h2>
        </div>
        <div className="range-picker" aria-label="Dĺžka časovej rady">
          {seriesRanges.map(([days, label]) => (
            <button className={range === days ? 'active' : ''} key={days}
              onClick={() => { setRange(days); setSelectedDate(null) }}>{label}</button>
          ))}
        </div>
      </div>
      <div className="series-readout" aria-live="polite">
        <strong>{formatDate(selected?.date, { year: 'numeric' })}</strong>
        <span><CloudRain size={14} /> {selected?.rain_mm ?? '—'} mm</span>
        <span><ThermometerSun size={14} /> {selected?.temperature_min_c ?? '—'} / {selected?.temperature_c ?? '—'} / {selected?.temperature_max_c ?? '—'} °C</span>
        <span><Gauge size={14} /> {selected?.pressure_hpa ?? '—'} hPa</span>
      </div>
      <div className="series-scroll">
        <svg className="weather-series" viewBox="0 0 1000 350" preserveAspectRatio="none"
          role="slider" tabIndex="0" aria-label={`Denné počasie za ${range} dní; šípkami vyber deň.`}
          aria-valuemin="0" aria-valuemax={visible.length - 1} aria-valuenow={selectedIndex}
          aria-valuetext={`${formatDate(selected.date)}: ${selected.rain_mm ?? 0} mm`}
          onKeyDown={(event) => moveChartSelection(event, visible, selectedIndex, setSelectedDate)}
          onPointerMove={(event) => { if (event.buttons === 1) selectFromPointer(event) }} onClick={selectFromPointer}>
          <g className="series-grid">
            {[18, 98, 126, 218, 246, 330].map((value) => <line x1="0" x2="1000" y1={value} y2={value} key={value} />)}
          </g>
          <g className="rain-bars">
            {visible.map((item, index) => {
              const height = ((item.rain_mm || 0) / maxRain) * 72
              return <rect key={item.date} x={x(index)} y={96 - height} width={Math.max(.45, 800 / visible.length)} height={height} />
            })}
          </g>
          <polyline className="temperature-min" points={line('temperature_min_c', 130, 214, temperatureDomain)} />
          <polyline className="temperature-max" points={line('temperature_max_c', 130, 214, temperatureDomain)} />
          <polyline className="temperature-mean" points={line('temperature_c', 130, 214, temperatureDomain)} />
          <polyline className="pressure-line" points={line('pressure_hpa', 250, 326, pressureDomain)} />
          <line className="selected-line" x1={x(selectedIndex)} x2={x(selectedIndex)} y1="10" y2="334" />
          <g className="series-labels" aria-hidden="true">
            <text x="8" y="15">ZRÁŽKY · mm</text>
            <text x="8" y="124">TEPLOTA · min / priemer / max °C</text>
            <text x="8" y="244">TLAK · hPa</text>
          </g>
        </svg>
        <div className="series-dates">
          <time>{formatDate(visible[0].date, { year: 'numeric' })}</time>
          <span>Klikni na graf pre presné hodnoty</span>
          <time>{formatDate(visible.at(-1).date, { year: 'numeric' })}</time>
        </div>
      </div>
    </section>
  )
}

const MONTH_STARTS = [1, 32, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335]
const MONTH_LETTERS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']

// Units for the response-curve axes, matched on the feature name. The model
// itself is unit-agnostic; a reader is not.
function featureUnit(feature) {
  if (!feature) return ''
  if (/^rain_lag|^rain_\d|_signal$|^water_balance|^et0|^sand_moisture|^loam_moisture/.test(feature)) return 'mm'
  if (/^days_since|^dry_days|^hot_days|^wet_days/.test(feature)) return 'dní'
  if (/^temp_|^night_lowest|^night_highest|^day_highest/.test(feature)) return '°C'
  if (/^rh_/.test(feature)) return '%'
  if (/^antecedent_heat_load/.test(feature)) return '°C·dní nad 26'
  if (/^night_optimality/.test(feature)) return ''
  if (/^pressure/.test(feature)) return 'hPa'
  return ''
}

const oddsMultiplier = (logOdds) => {
  const factor = Math.exp(logOdds)
  return factor >= 10 ? `×${factor.toFixed(0)}` : `×${factor.toFixed(2)}`
}

// One response curve, drawn on its own axis. Log-odds above zero means this
// value of this input pushes the score up relative to a typical day. The area
// between the curve and zero is filled so "where does this input help, where
// does it hurt" is readable at a glance, and the axis is labelled in odds
// multipliers, which is what log-odds mean.
function ShapeCurve({ curve, strongest, asOf }) {
  const points = curve.points
  const values = points.map((point) => point.value)
  const effects = points.map((point) => point.log_odds)
  const minimum = Math.min(...values)
  const maximum = Math.max(...values)
  const lowest = Math.min(...effects, 0)
  const highest = Math.max(...effects, 0)
  const span = Math.max(highest - lowest, 0.05)
  const width = 260
  const height = 92
  const pad = 32
  const x = (value) => pad + ((value - minimum) / Math.max(maximum - minimum, 1e-9)) * (width - pad * 2)
  const y = (effect) => pad + (height - pad * 2) - ((effect - lowest) / span) * (height - pad * 2)
  // Each curve is drawn on its own vertical scale, so a weak term would look as
  // dramatic as a decisive one. This bar carries the missing comparison.
  const strength = strongest > 0 ? curve.mean_absolute_log_odds / strongest : 0
  const path = points.map((point) => `${x(point.value).toFixed(1)},${y(point.log_odds).toFixed(1)}`).join(' ')
  const closed = `${x(points[0].value).toFixed(1)},${y(0).toFixed(1)} ${path} ${x(points.at(-1).value).toFixed(1)},${y(0).toFixed(1)}`
  // Drop an extreme tick that would print on top of the ×1 line.
  const effectTicks = [...new Set([lowest, 0, highest].map((value) => Number(value.toFixed(2))))]
    .filter((tick) => tick === 0 || Math.abs(y(tick) - y(0)) > 9)
  const seasonal = curve.unit === 'day_of_year'
  const unit = seasonal ? '' : featureUnit(curve.feature)
  const axisValue = (value) => Number(value).toLocaleString('sk-SK', { maximumFractionDigits: Math.abs(value) >= 20 ? 0 : 1 })
  const clipId = `shape-clip-${curve.feature}`
  const todayValue = curve.today_value
  const todayEffect = Number.isFinite(todayValue)
    ? points.reduce((best, point) => (
      Math.abs(point.value - todayValue) < Math.abs(best.value - todayValue) ? point : best
    ), points[0])
    : null
  const todayLabel = seasonal
    ? formatDate(asOf)
    : `${axisValue(todayValue)} ${unit}`.trim()

  return (
    <figure className="shape-curve">
      <figcaption>
        <strong>{curve.label}</strong>
        {curve.monotonic !== 0 && (
          <em title="Smer tohto vplyvu je pevne daný ekológiou, model ho nemôže otočiť.">
            {curve.monotonic > 0 ? 'rastúci ↑' : 'klesajúci ↓'}
          </em>
        )}
      </figcaption>
      <div className="shape-strength" title={`Priemerný vplyv tohto člena je ${Math.round(strength * 100)} % najsilnejšieho v modeli.`}>
        <i style={{ width: `${Math.max(3, strength * 100)}%` }} />
        <span>sila člena {Math.round(strength * 100)} %</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height + 16}`} role="img"
        aria-label={`Vplyv veličiny ${curve.label} na log-šance modelu oproti typickému dňu`}>
        <defs>
          <clipPath id={`${clipId}-up`}><rect x={pad} y={0} width={width - pad * 2} height={y(0)} /></clipPath>
          <clipPath id={`${clipId}-down`}><rect x={pad} y={y(0)} width={width - pad * 2} height={height - y(0)} /></clipPath>
        </defs>
        <polygon className="shape-fill shape-fill--up" points={closed} clipPath={`url(#${clipId}-up)`} />
        <polygon className="shape-fill shape-fill--down" points={closed} clipPath={`url(#${clipId}-down)`} />
        {effectTicks.map((tick) => (
          <g key={tick}>
            <line className={tick === 0 ? 'shape-zero shape-zero--base' : 'shape-zero'} x1={pad} x2={width - pad} y1={y(tick)} y2={y(tick)} />
            <text className="shape-axis" x={pad - 4} y={y(tick) + 3} textAnchor="end">
              {tick === 0 ? '×1' : oddsMultiplier(tick)}
            </text>
          </g>
        ))}
        <polyline className="shape-line" points={path} />
        {todayEffect && (
          <g className="shape-today">
            <line x1={x(todayEffect.value)} x2={x(todayEffect.value)} y1={pad} y2={height - pad} />
            <circle cx={x(todayEffect.value)} cy={y(todayEffect.log_odds)} r="3.5" />
          </g>
        )}
        {seasonal
          ? MONTH_STARTS.map((doy, index) => (
            <text className="shape-axis" key={doy} x={x(doy)} y={height + 12} textAnchor="middle">{MONTH_LETTERS[index]}</text>
          ))
          : (
            <>
              <text className="shape-axis" x={pad} y={height + 12}>{axisValue(minimum)} {unit}</text>
              <text className="shape-axis" x={width - pad} y={height + 12} textAnchor="end">{axisValue(maximum)} {unit}</text>
            </>
          )}
      </svg>
      {todayEffect ? (
        <small className={`shape-reading ${todayEffect.log_odds >= 0 ? 'is-up' : 'is-down'}`}>
          Dnes {todayLabel}: šance {oddsMultiplier(todayEffect.log_odds)} oproti typickému dňu
        </small>
      ) : (
        <small className="shape-y-note">Zvisle: násobok šancí oproti typickému dňu (×1 = bez vplyvu)</small>
      )}
    </figure>
  )
}

function ModelShapePanel({ curves, asOf }) {
  const [expanded, setExpanded] = useState(false)
  if (!curves?.length) return null
  const visible = expanded ? curves : curves.slice(0, 6)
  const strongest = Math.max(...curves.map((curve) => curve.mean_absolute_log_odds), 0)
  return (
    <section className="shape-panel" id="tvar-modelu">
      <div className="section-heading">
        <div>
          <span className="eyebrow"><Sprout size={15} /> Celý model, nie ukážka</span>
          <h2>Čo model robí s každou veličinou</h2>
        </div>
        <span className="period">{curves.length} členov</span>
      </div>
      <p className="shape-intro">
        Skóre dňa je súčet týchto kriviek — nič viac. Každá ukazuje, ako daná
        veličina sama o sebe posúva výsledok oproti typickému dňu. Zvislá čiara
        je hodnota k {formatDate(asOf)}. Toto nie je zjednodušený obrázok
        zložitejšieho modelu; toto <em>je</em> model.
      </p>
      <div className="shape-grid">
        {visible.map((curve) => (
          <ShapeCurve curve={curve} strongest={strongest} asOf={asOf} key={curve.feature} />
        ))}
      </div>
      {curves.length > 6 && (
        <button className="shape-toggle" onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Zobraziť len najsilnejšie' : `Zobraziť všetkých ${curves.length} členov`}
        </button>
      )}
    </section>
  )
}

function RainMap({ stations }) {
  const plotted = stations.filter((station) => (
    station.latitude >= 48.2 && station.latitude <= 49.1
    && station.longitude >= 16.75 && station.longitude <= 17.4
  ))
  const [active, setActive] = useState(plotted[0] || null)
  const point = (station) => ({
    x: 24 + ((station.longitude - 16.75) / 0.65) * 272,
    y: 20 + ((49.1 - station.latitude) / 0.9) * 290,
  })
  const markerColor = (rain) => {
    if (rain >= 10) return '#d9be4c'
    if (rain >= 5) return '#89a947'
    if (rain >= 2) return '#508271'
    return '#94aaa0'
  }

  return (
    <div className="rain-map-wrap">
      <span className="map-note">schematická mapa</span>
      <svg className="rain-map" viewBox="0 0 320 330" role="img" aria-labelledby="rain-map-title rain-map-desc">
        <title id="rain-map-title">Mapa zrážkomerných staníc na Záhorí</title>
        <desc id="rain-map-desc">Body zobrazujú úhrn zrážok za posledných 24 hodín.</desc>
        <defs>
          <pattern id="forest-pattern" width="16" height="16" patternUnits="userSpaceOnUse">
            <circle cx="4" cy="5" r="1.2" fill="#6e8a56" opacity=".34" />
            <circle cx="12" cy="12" r="1" fill="#6e8a56" opacity=".25" />
          </pattern>
          <linearGradient id="terrain-fill" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#e8ead8" />
            <stop offset="1" stopColor="#cfd9bd" />
          </linearGradient>
        </defs>

        <path
          className="map-region"
          d="M176 23 C205 42 231 68 237 97 C247 128 266 151 270 184 C276 220 263 245 239 267 C219 285 201 306 177 315 C151 307 128 288 100 281 C75 274 56 254 55 228 C53 200 69 179 75 153 C82 126 76 102 90 80 C110 50 143 34 176 23 Z"
          fill="url(#terrain-fill)"
        />
        <path
          className="map-forest"
          d="M170 33 C201 56 221 78 225 108 C232 144 258 165 253 203 C249 238 220 265 192 289 C173 305 154 298 137 285 C166 255 181 227 185 193 C189 151 176 108 170 33 Z"
          fill="url(#forest-pattern)"
        />
        <path className="map-river" d="M91 56 C69 88 85 115 73 147 C61 180 48 211 59 246 C65 266 82 277 101 283" />
        <path className="map-ridge" d="M204 49 C222 85 225 117 245 148 C268 183 260 226 233 259 C217 279 196 299 177 312" />
        <path className="map-road" d="M180 73 C164 115 160 158 157 198 C155 235 164 271 177 307" />

        <g className="map-labels" aria-hidden="true">
          <text x="198" y="77">Skalica</text>
          <text x="115" y="191">Moravský Sv. Ján</text>
          <text x="145" y="245">Malacky</text>
          <text x="190" y="302">Bratislava</text>
          <text className="river-label" x="48" y="178" transform="rotate(-77 48 178)">Morava</text>
          <text className="ridge-label" x="264" y="185" transform="rotate(77 264 185)">Malé Karpaty</text>
        </g>

        <g className="north-mark" aria-hidden="true">
          <path d="M286 42 L292 25 L298 42 L292 38 Z" />
          <text x="292" y="18" textAnchor="middle">S</text>
        </g>

        <g className="map-stations">
          {plotted.map((station) => {
            const { x, y } = point(station)
            const radius = 4.5 + Math.min(station.rain_24h_mm, 15) * 0.32
            const selected = active?.id === station.id
            return (
              <g
                className={`map-station ${selected ? 'is-active' : ''}`}
                key={station.id}
                transform={`translate(${x} ${y})`}
                role="button"
                tabIndex="0"
                aria-label={`${station.name}, ${station.rain_24h_mm} milimetra`}
                onMouseEnter={() => setActive(station)}
                onFocus={() => setActive(station)}
                onClick={() => setActive(station)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') setActive(station)
                }}
              >
                <title>{station.name}: {station.rain_24h_mm} mm</title>
                <circle className="station-halo" r={radius + 4} />
                <circle r={radius} fill={markerColor(station.rain_24h_mm)} />
                <circle className="station-core" r="2" />
              </g>
            )
          })}
        </g>
      </svg>

      <div className="map-readout" aria-live="polite">
        <span><MapPin size={13} /> Vybraná stanica</span>
        <strong>{active?.name || '—'}</strong>
        <b>{active?.rain_24h_mm ?? '—'} <small>mm / 24 h</small></b>
      </div>
      <div className="map-legend" aria-label="Legenda zrážok">
        <span><i className="rain-low" /> 0–2</span>
        <span><i className="rain-mid" /> 2–5</span>
        <span><i className="rain-high" /> 5–10</span>
        <span><i className="rain-heavy" /> 10+ mm</span>
      </div>
    </div>
  )
}

function browserMapLinks(item) {
  if (!item?.latitude || !item?.longitude) return {}
  if (item.navigation_verified === false) {
    return { openstreetmap_url: item.search_zone_map_url }
  }
  const coordinates = `${item.latitude},${item.longitude}`
  return {
    google_maps_url: item.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coordinates)}`,
    google_directions_url: item.google_directions_url || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(coordinates)}&travelmode=driving`,
    openstreetmap_url: item.openstreetmap_url || `https://www.openstreetmap.org/?mlat=${item.latitude}&mlon=${item.longitude}#map=15/${item.latitude}/${item.longitude}`,
  }
}

function CombinedRegionMap({ areas, stations, active, onSelect, layers }) {
  const container = useRef(null)
  const map = useRef(null)
  const overlays = useRef([])
  const markers = useRef(new Map())
  // Bumped whenever markers are rebuilt, so the highlight effect below reruns
  // and the current selection does not silently lose its ring on a layer toggle.
  const [rebuild, setRebuild] = useState(0)

  useEffect(() => {
    if (!container.current || map.current) return
    map.current = L.map(container.current, {
      center: [48.55, 17.08],
      zoom: 9,
      minZoom: 8,
      maxZoom: 18,
      zoomControl: true,
    })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map.current)
    L.control.scale({ imperial: false, position: 'bottomright' }).addTo(map.current)
    map.current.fitBounds([[48.21, 16.79], [48.91, 17.4]], { padding: [18, 18] })
    return () => {
      map.current?.remove()
      map.current = null
    }
  }, [])

  useEffect(() => {
    if (!map.current) return
    overlays.current.forEach((layer) => layer.remove())
    overlays.current = []
    markers.current.clear()
    const add = (layer) => {
      layer.addTo(map.current)
      overlays.current.push(layer)
      return layer
    }

    if (layers.stations) {
      stations.forEach((station) => {
        const rain = Number(station.rain_24h_mm || 0)
        const color = rain >= 10 ? '#d9be4c' : rain >= 5 ? '#89a947' : rain >= 2 ? '#508271' : '#80998e'
        const marker = add(L.circleMarker([station.latitude, station.longitude], {
          radius: 5 + Math.min(rain, 15) * .2,
          color: '#ffffff',
          weight: 1.5,
          fillColor: color,
          fillOpacity: .9,
        })).bindTooltip(`${station.name}: ${rain} mm / 24 h`).on('click', () => onSelect(station))
        markers.current.set(`station:${station.id}`, { marker, kind: 'station', item: station })
      })
    }

    if (layers.locations) {
      areas.forEach((area, index) => {
        const blocked = !area.accessible
        if (area.access_point) {
          add(L.polyline([
            [area.access_point.latitude, area.access_point.longitude],
            [area.latitude, area.longitude],
          ], { color: '#315f78', weight: 2.5, dashArray: '6 7', opacity: .75 }))
          const accessMarker = add(L.circleMarker([area.access_point.latitude, area.access_point.longitude], {
            radius: 7,
            color: '#ffffff',
            weight: 2.5,
            fillColor: '#315f78',
            fillOpacity: .98,
          })).bindTooltip(`${area.parking_verified ? 'Parkovanie' : 'Lesný nástup'}: ${area.access_point.name}`).on('click', () => onSelect(area))
          markers.current.set(`access:${area.id}`, { marker: accessMarker, kind: 'access', item: area })
        }
        const areaMarker = add(L.circleMarker([area.latitude, area.longitude], {
          radius: 9,
          color: blocked ? '#873b31' : '#38512d',
          weight: 2.5,
          fillColor: blocked ? '#c36b5d' : '#b5d64a',
          fillOpacity: .96,
        })).bindTooltip(`Lesná zóna: ${area.name} · poradie č. ${index + 1}${blocked ? ' · nevstupovať' : ''}`).on('click', () => onSelect(area))
        markers.current.set(`area:${area.id}`, { marker: areaMarker, kind: 'area', item: area })
      })
    }
    setRebuild((value) => value + 1)
  }, [areas, layers, onSelect, stations])

  useEffect(() => {
    markers.current.forEach(({ marker, kind, item }) => {
      const selected = active?.id === item.id
      if (kind === 'station') {
        const rain = Number(item.rain_24h_mm || 0)
        marker.setRadius(selected ? 10 : 5 + Math.min(rain, 15) * .2)
        marker.setStyle({ color: selected ? '#18372d' : '#ffffff', weight: selected ? 3 : 1.5 })
      } else if (kind === 'access') {
        marker.setRadius(selected ? 9 : 7)
      } else {
        marker.setRadius(selected ? 13 : 9)
        marker.setStyle({ weight: selected ? 4 : 2.5 })
      }
    })
  }, [active?.id, rebuild])

  return <div className="leaflet-region-map" ref={container} aria-label="Interaktívna mapa Záhoria s cestami, lesnými trasami, lokalitami a zrážkami" />
}

function accessWindow(access) {
  if (!access || access.status === 'verify') return 'overiť'
  if (access.status === 'blocked' || !access.windows?.length) return 'bez okna'
  return access.windows.join(' + ')
}

function RegionMap({ stations, military, guide, forecastPoints, accessCalendar }) {
  const plotted = useMemo(() => stations.filter((station) => (
    station.latitude >= 48.2 && station.latitude <= 49.1
    && station.longitude >= 16.75 && station.longitude <= 17.4
  )), [stations])
  const [guideData, setGuideData] = useState(guide)
  const [pendingDate, setPendingDate] = useState(null)
  const [guideLoading, setGuideLoading] = useState(false)
  const [guideError, setGuideError] = useState(false)
  const [active, setActive] = useState(guide?.recommendations?.[0] || plotted[0] || null)
  const [layers, setLayers] = useState({ locations: true, stations: true })
  const guideRequest = useRef(null)
  const selectedDate = pendingDate || guideData?.date || guide?.date
  const selectGuideDate = (date) => {
    if (!date || date === guideData?.date) return
    guideRequest.current?.abort()
    const controller = new AbortController()
    guideRequest.current = controller
    setPendingDate(date)
    setGuideLoading(true)
    setGuideError(false)
    fetch(`/api/guide?date=${date}`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Guide request failed')
        return response.json()
      })
      .then((payload) => {
        setGuideData(payload)
        setActive(payload.recommendations?.[0] || payload.all_areas?.[0] || null)
      })
      .catch((error) => { if (error.name !== 'AbortError') setGuideError(true) })
      .finally(() => {
        if (!controller.signal.aborted) {
          setGuideLoading(false)
          setPendingDate(null)
        }
      })
  }
  const allAreas = useMemo(() => guideData?.all_areas || [], [guideData])
  const areas = useMemo(
    () => allAreas.filter((area) => area.navigation_verified),
    [allAreas],
  )
  const hiddenUnverifiedCount = allAreas.length - areas.length
  const calendarDays = useMemo(
    () => (forecastPoints || []).filter((item) => item.day_offset >= 0),
    [forecastPoints],
  )
  if (!areas.length) {
    return (
      <section className="region-map-panel" id="mapa">
        <div className="section-heading"><div><span className="eyebrow"><Navigation size={15} /> Záhorie</span><h2>Mapa zrážok</h2></div></div>
        <RainMap stations={stations} />
      </section>
    )
  }
  const selectedAccess = (guideData?.access_calendar || accessCalendar || []).find((day) => day.date === selectedDate)
  const danger = !selectedAccess?.schedule_known
    || selectedAccess?.north?.status === 'blocked'
    || selectedAccess?.south?.status === 'blocked'
  const activeLinks = browserMapLinks(active)
  const activeRank = active?.score !== undefined ? areas.findIndex((area) => area.id === active.id) + 1 : null

  return (
    <section className="region-map-panel" id="mapa">
      <div className="section-heading map-heading">
        <div>
          <span className="eyebrow"><Navigation size={15} /> Celé Záhorie</span>
          <h2>Kam ísť a kam nevstupovať</h2>
        </div>
        <span className="map-note">OpenStreetMap · cesty a lesné trasy</span>
      </div>

      <div className="map-calendar" aria-label="Vyber deň mapovej predpovede">
        {calendarDays.map((day) => {
          const accessDay = (guideData?.access_calendar || accessCalendar || []).find((item) => item.date === day.date)
          const accessLabel = !accessDay?.schedule_known ? 'VO: overiť'
            : accessDay.north.status === 'blocked' && accessDay.south.status === 'blocked' ? 'VO: bez okna'
              : accessDay.north.status === 'limited' || accessDay.south.status === 'limited' ? 'VO: časť dňa'
                : 'VO: za svetla'
          return (
            <button className={selectedDate === day.date ? 'active' : ''} key={day.date}
              onClick={() => selectGuideDate(day.date)} disabled={guideLoading && selectedDate === day.date}>
              <span>{day.day_offset === 0 ? 'Dnes' : new Intl.DateTimeFormat('sk-SK', { weekday: 'short' }).format(new Date(`${day.date}T12:00:00`))}</span>
              <strong>{new Date(`${day.date}T12:00:00`).getDate()}</strong>
              <small>{day.chance_percent}/100</small>
              <em>{accessLabel}</em>
            </button>
          )
        })}
      </div>

      <div className={`military-banner ${danger ? 'is-danger' : ''}`}>
        <ShieldAlert size={22} />
        <div>
          <strong>{selectedAccess ? `${formatDate(selectedDate)} · svetlo ${selectedAccess.sunrise}–${selectedAccess.sunset}: sever ${accessWindow(selectedAccess.north)} · juh ${accessWindow(selectedAccess.south)}` : `${formatDate(selectedDate)} nie je v publikovanej časti harmonogramu`}</strong>
          <span>Časy zákazov preberáme z aktuálneho harmonogramu MOD. Platí iba pre VO Záhorie; odporúčané lesné zóny sú civilné.</span>
        </div>
        <div className="military-actions">
          <a className="primary" href={military?.source_url} target="_blank" rel="noreferrer">Aktuálny harmonogram MOD <ExternalLink size={13} /></a>
          <a href="https://www.mosr.sk/data/att/163313.jpg" target="_blank" rel="noreferrer">Mapa hraníc MO SR <ExternalLink size={13} /></a>
        </div>
      </div>

      {guideError && <div className="guide-error">Predpoveď pre tento deň sa nepodarilo načítať. Skús deň vybrať znovu.</div>}
      <div className="map-layer-controls" aria-label="Mapové vrstvy">
        {[
          ['locations', `${areas.length} overených štartov`],
          ['stations', 'Zrážky SHMÚ'],
        ].map(([key, label]) => (
          <button className={layers[key] ? 'active' : ''} key={key} type="button"
            aria-pressed={layers[key]} onClick={() => setLayers((current) => ({ ...current, [key]: !current[key] }))}>
            <i /> {label}
          </button>
        ))}
        <span>Hranicu VO nekreslíme približne — otvor oficiálnu mapu MO SR</span>
      </div>
      {hiddenUnverifiedCount > 0 && (
        <div className="spatial-audit-note">
          <Info size={15} /> {hiddenUnverifiedCount} starších obecných bodov je skrytých, kým neoveríme samostatný verejný prístup. Navigácia ich už neponúka.
        </div>
      )}
      <div className={`region-map-wrap ${guideLoading ? 'is-loading' : ''}`}>
        <CombinedRegionMap areas={areas} stations={plotted} active={active} onSelect={setActive} layers={layers} />

        <div className="region-readout" aria-live="polite">
          <span><MapPin size={13} /> {active?.score !== undefined ? 'Overený štart + lesná zóna' : 'Stanica SHMÚ'}</span>
          <strong>{active?.name || '—'}</strong>
          {active?.score !== undefined ? (
            <>
              <b>#{activeRank} <small>expertné poradie · iba za svetla {active.access_windows?.join(' + ') || `${active.sunrise}–${active.sunset}`}</small></b>
              <div className="coordinate-pair">
                <span><b>Štart / parkovanie</b><code>{active.access_gps}</code></span>
                <span><b>Lesná zóna</b><code>{active.search_zone_gps}</code></span>
              </div>
              <small className="walk-note">Pešo približne {active.walk_to_search_zone_km} km · {active.access_point?.name}</small>
              {active.local_weather && <small className="local-weather-note">Lokálna bunka: {active.local_weather.rain_14d_mm} mm / 14 dní · noc {active.local_weather.temperature_min_c} °C</small>}
              <p>{active.reasoning?.slice(0, 3).join(' · ')}</p>
              <div className="species-links">
                {(active.species || []).map((species) => (
                  <a href={species.wikipedia_url} target="_blank" rel="noreferrer" key={species.wikipedia_url}
                    title={species.scientific_name}>{species.name} <ExternalLink size={9} /></a>
                ))}
              </div>
            </>
          ) : <b>{active?.rain_24h_mm ?? '—'} <small>mm / 24 h</small></b>}
          {active?.latitude && (
            <div className="map-target-actions">
              {active?.score !== undefined ? (
                <>
                  <a className="primary" href={activeLinks.google_directions_url} target="_blank" rel="noreferrer"><Navigation size={12} /> Navigovať na štart</a>
                  <a className="walking" href={active.google_walking_url} target="_blank" rel="noreferrer"><Navigation size={12} /> Pešo do lesnej zóny</a>
                  <a href={active.google_search_zone_url} target="_blank" rel="noreferrer">Lesná zóna v Google Maps</a>
                  <a href={active.search_zone_map_url} target="_blank" rel="noreferrer">Zóna v OSM</a>
                  <a href={active.access_point?.osm_element_url} target="_blank" rel="noreferrer">
                    {active.parking_verified ? 'Overenie parkoviska' : 'Overenie lesného nástupu'}
                  </a>
                </>
              ) : (
                <>
                  <a href={activeLinks.google_maps_url} target="_blank" rel="noreferrer">Stanica v Google Maps</a>
                  <a href={activeLinks.openstreetmap_url} target="_blank" rel="noreferrer">Stanica v OSM</a>
                </>
              )}
            </div>
          )}
        </div>
        <div className="region-legend"><span><i className="access-dot" /> štart</span><span><i className="guide-dot" /> lesná zóna</span><span><i className="station-dot" /> zrážky</span></div>
      </div>

      <div className="map-bottom">
        <div className="tomorrow-list">
          <span>Overené východiská na {formatDate(guideData?.date)}</span>
          {(guideData?.recommendations || []).slice(0, 5).map((area, index) => {
            const links = browserMapLinks(area)
            return (
              <article className="recommendation-row" key={area.id}>
                <button className="recommendation-main" onClick={() => setActive(area)}>
                  <b>{index + 1}</b><span><strong>{area.name}</strong><small>štart {area.access_gps} · pešo {area.walk_to_search_zone_km} km</small></span><em>{area.access_windows?.[0] || 'za svetla'}</em>
                </button>
                <div>
                  {(area.species || []).slice(0, 3).map((species) => (
                    <a className="species-chip" href={species.wikipedia_url} target="_blank" rel="noreferrer"
                      title={`${species.name} · ${species.scientific_name}`} key={species.wikipedia_url}>{species.name}</a>
                  ))}
                  <a className="recommendation-nav" href={links.google_directions_url} target="_blank" rel="noreferrer">
                    <Navigation size={12} /> Trasa
                  </a>
                </div>
              </article>
            )
          })}
        </div>
        <div className="schedule-list">
          <span>Harmonogram VO Záhorie · sever C–F / juh A–B</span>
          {(guideData?.access_calendar || accessCalendar || []).map((day) => {
            const schedule = (military?.upcoming || []).find((item) => item.date === day.date)
            return (
              <div key={day.date} className={!day.schedule_known || day.north.status !== 'daylight' || day.south.status !== 'daylight' ? 'restricted' : ''}>
                <time>{formatDate(day.date, { weekday: 'short' })}</time>
                <span>
                  <small>Zákaz: S {schedule?.north || '—'} · J {schedule?.south || '—'} · svetlo {day.sunrise}–{day.sunset}</small>
                  <strong>S {accessWindow(day.north)} · J {accessWindow(day.south)}</strong>
                </span>
              </div>
            )
          })}
          <a className="schedule-source" href={military?.source_url} target="_blank" rel="noreferrer">Aktuálny harmonogram zákazov MOD <ExternalLink size={12} /></a>
          <a href="https://www.mosr.sk/data/att/163313.jpg" target="_blank" rel="noreferrer">Oficiálna mapa VO Záhorie <ExternalLink size={12} /></a>
          <a href={military?.rules_and_maps_url} target="_blank" rel="noreferrer">Oficiálne hranice a pravidlá <ExternalLink size={12} /></a>
          <p>Zdroj aktualizovaný {military?.last_updated || '—'}. Okno je prienik denného svetla a času mimo publikovaného zákazu. Pomlčka v harmonograme znamená, že pre daný sektor nie je uvedený zákaz cvičenia; stále nejde o samostatné povolenie vstupu. Rozhoduje harmonogram, mapa, tabule a pokyny.</p>
        </div>
      </div>
      <p className="map-disclaimer">Podklad OpenStreetMap zobrazuje cesty a lesné trasy, ale nepotvrdzuje právo vstupu ani zjazdnosť. Neoverený približný polygon VO sme odstránili. Hranicu, sektory a trvalé zákazy vždy čítaj z oficiálnej mapy MO SR; rozhoduje tiež aktuálny harmonogram, tabule a pokyny v teréne.</p>
    </section>
  )
}

function RainTimeline({ context, analysis, asOf }) {
  const points = context.timeline || []
  const maxRain = Math.max(...points.map((item) => item.rain_max_mm), 1)
  const bestDelay = analysis.rain_delay?.empirical_best
  const bestNight = analysis.night_temperature?.empirical_best
  const today = new Date(`${asOf}T12:00:00`)

  const windowState = (window) => {
    const start = new Date(`${window.start_date}T12:00:00`)
    const end = new Date(`${window.end_date}T12:00:00`)
    if (today < start) return 'čoskoro'
    if (today <= end) return 'práve teraz'
    return 'po okne'
  }

  return (
    <section className="panel timing-panel" id="casovanie">
      <div className="section-heading timing-heading">
        <div>
          <span className="eyebrow"><Droplets size={15} /> Dážď → podhubie → plodnice</span>
          <h2>Rozhoduje, kedy pršalo</h2>
        </div>
        <div className="phase-pill">
          <i /> {phaseCopy[context.phase] || context.phase}
        </div>
      </div>

      <div className="timing-summary">
        <div><span>Posledné výdatné zrážky</span><strong>{formatDate(context.last_soaking_rain_date)}</strong></div>
        <div><span>Od dažďa</span><strong>{context.days_since_soaking_rain} dní</strong></div>
        <div><span>Sucho pred dažďom</span><strong>{context.dry_days_before_soaking} dní</strong></div>
        <div><span>Vhodné noci / 7 dní</span><strong>{context.optimal_nights_7d} zo 7</strong></div>
      </div>

      <div className="timeline-scroll" tabIndex="0" aria-label="Časová os zrážok a nočných teplôt">
        <div className="rain-timeline">
          {points.map((item) => {
            const isToday = item.date === asOf
            const height = Math.max(2, (item.rain_max_mm / maxRain) * 92)
            return (
              <div className={`rain-day ${item.forecast ? 'is-forecast' : ''} ${isToday ? 'is-today' : ''}`} key={item.date}>
                <div className="rain-bar-area" title={`${formatDate(item.date)}: ${item.rain_max_mm} mm, noc ${item.night_temperature_c} °C`}>
                  <i style={{ height: `${height}%` }} />
                </div>
                <b>{item.night_temperature_c}°</b>
                <time>{new Date(`${item.date}T12:00:00`).getDate()}</time>
                {isToday && <span>Dnes</span>}
              </div>
            )
          })}
        </div>
        <div className="timeline-legend">
          <span><i /> namerané</span>
          <span><i className="forecast-swatch" /> predpoveď</span>
          <span>číslo pod stĺpcom = nočné minimum °C</span>
        </div>
      </div>

      <div className="species-windows">
        {(context.species_windows || []).map((window) => (
          <article key={window.group}>
            <div>
              <span>{window.habitat}</span>
              <h3>{window.group}</h3>
            </div>
            <div className="window-date">
              <strong>{formatDate(window.start_date)} – {formatDate(window.end_date)}</strong>
              <span className={`window-state window-state--${windowState(window).replace(' ', '-')}`}>{windowState(window)}</span>
            </div>
          </article>
        ))}
      </div>

      <div className="knowledge-row">
        <div className="learned-condition">
          <span>Čo našlo 5 rokov dát</span>
          <p>Najsilnejšie pásmo po výdatnom daždi: <strong>{bestDelay?.band || '—'} dní</strong>. Najsilnejšie 7-dňové nočné minimum: <strong>{bestNight?.band || '—'} °C</strong>.</p>
        </div>
        <div className="literature-links">
          <span>Krížová kontrola s výskumom</span>
          {(analysis.knowledge || []).map((item) => (
            <a href={item.url} target="_blank" rel="noreferrer" key={item.url} title={item.claim}>
              {item.source} <ExternalLink size={11} />
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}

function Stations({ stations }) {
  const visible = stations.slice(0, 8)
  const max = Math.max(...visible.map((station) => station.rain_24h_mm), 1)
  return (
    <section className="panel stations-panel" id="zrazky">
      <div className="section-heading">
        <div>
          <span className="eyebrow"><CloudRain size={15} /> Oficiálne stanice SHMÚ</span>
          <h2>Kde pršalo najviac</h2>
        </div>
        <span className="period">posledných 24 h</span>
      </div>
      <div>
        <div className="station-list">
          {visible.map((station) => (
            <div className="station-row" key={station.id}>
              <div className="station-name">
                <MapPin size={15} />
                <span>{station.name}</span>
              </div>
              <div className="station-bar"><i style={{ width: `${(station.rain_24h_mm / max) * 100}%` }} /></div>
              <strong>{station.rain_24h_mm}<small> mm</small></strong>
            </div>
          ))}
        </div>
        <a className="text-link" href="https://www.shmu.sk/sk/?id=hydro_zra_all&page=1&uhrny=24" target="_blank" rel="noreferrer">
          Všetky stanice na SHMÚ <ExternalLink size={14} />
        </a>
      </div>
    </section>
  )
}

function NewsFeed({ news }) {
  const [filter, setFilter] = useState('all')
  const filtered = useMemo(() => {
    if (filter === 'all') return news
    if (filter === 'conditions') return news.filter((item) => item.kind !== 'observation')
    return news.filter((item) => item.kind === 'observation')
  }, [filter, news])

  return (
    <section className="news-section" id="spravy">
      <div className="section-heading news-heading">
        <div>
          <span className="eyebrow"><Activity size={15} /> Živé signály</span>
          <h2>Hubárske správy zo Záhoria</h2>
        </div>
        <div className="filters" aria-label="Filtrovať správy">
          {[
            ['all', 'Všetko'],
            ['conditions', 'Podmienky'],
            ['observation', 'Nálezy'],
          ].map(([value, label]) => (
            <button className={filter === value ? 'active' : ''} onClick={() => setFilter(value)} key={value}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="news-grid">
        {filtered.slice(0, 8).map((item, index) => {
          const content = (
            <>
              <div className={`news-icon news-icon--${item.kind}`}>
                {item.kind === 'observation' ? <Leaf /> : item.kind === 'rain' ? <CloudRain /> : <Compass />}
              </div>
              <div className="news-content">
                <div className="news-meta">
                  <span>{kindCopy[item.kind] || item.kind}</span>
                  <time>{formatDate(item.date)}</time>
                </div>
                <h3>{item.title}</h3>
                {item.scientific_name && <em>{item.scientific_name}</em>}
                <p>{item.summary}</p>
                <small>{item.source}</small>
              </div>
              {item.url && <ArrowRight className="news-arrow" size={19} />}
            </>
          )
          return item.url ? (
            <a className={`news-card ${index === 0 ? 'news-card--featured' : ''}`} href={item.url} target="_blank" rel="noreferrer" key={item.id}>
              {content}
            </a>
          ) : (
            <article className={`news-card ${index === 0 ? 'news-card--featured' : ''}`} key={item.id}>{content}</article>
          )
        })}
      </div>
      {!filtered.length && <p className="empty-state">Pre tento filter zatiaľ nemáme nové správy.</p>}
    </section>
  )
}

function MethodCard({ model }) {
  const auc = Number(model.validation.roc_auc || 0)
  const candidates = model.comparison?.candidates || []
  const legacy = candidates.find((item) => item.candidate_id === 'growth_weather_legacy')
  const engineered = candidates.find((item) => item.candidate_id === 'growth_weather_engineered')
  const spatial = model.spatial_alignment || {}
  const target = model.target_audit
  const explicit = model.explicit_validation
  const years = model.years || []
  const weatherAudit = model.weather_source_audit || {}
  const interpretability = model.interpretability || {}
  // The strongest opaque rival, for an honest side-by-side in the copy below.
  const opaqueRival = candidates
    .filter((item) => !item.interpretable && item.vs_production)
    .sort((a, b) => b.roc_auc - a.roc_auc)[0]
  const aucRange = model.validation.roc_auc_ci_95
  return (
    <aside className="panel method-card" id="metodika">
      <span className="eyebrow"><CheckCircle2 size={15} /> Časovo overené</span>
      <h2>Model, ktorému vidno pod kapotu</h2>
      <p>
        Model sa učí z potvrdených rastových dní a zo sezónne párovaného,
        neoznačeného pozadia. Neoznačený deň nie je vyhlásený za biologickú neprítomnosť.
        Testovanie ide dopredu v čase cez roky {years.join(', ')}.
      </p>
      {interpretability?.additive && (
        <div className="model-comparison interpretability-note">
          <span>Prečo aditívny model</span>
          <p>
            Skóre je súčet {interpretability.terms} samostatných kriviek, jedna
            na veličinu — <a href="#tvar-modelu">všetky sú tu vykreslené</a>.
            Nič sa nedeje „medzi“ nimi, takže rozklad každého dňa je presný, nie
            dodatočný odhad. Pri {interpretability.monotone_constrained_features?.length} veličinách
            je smer vplyvu pevne daný ekológiou.
          </p>
          {opaqueRival && (
            <p>
              Nepriehľadný ensemble dosiahol {Number(opaqueRival.roc_auc).toFixed(3)} AUC,
              teda rozdiel {opaqueRival.vs_production?.auc_difference > 0 ? '+' : ''}
              {opaqueRival.vs_production?.auc_difference} s intervalom{' '}
              {opaqueRival.vs_production?.auc_difference_ci_95?.join(' až ')}. Interval
              obsahuje nulu, takže presnosť sa nedá odlíšiť od náhody — a za takú cenu
              sa čitateľnosť nevymieňa.
            </p>
          )}
        </div>
      )}
      <div className="score-row">
        <div><strong>{auc.toFixed(3)}</strong><span>PU ROC AUC{aucRange ? ` · orientačne ${aucRange[0]}–${aucRange[1]}` : ''}</span></div>
        <div><strong>{Number(explicit?.pooled?.roc_auc || 0).toFixed(3)}</strong><span>sekundárne „rástli / nerástli“</span></div>
        <div><strong>{target?.positive_days?.toLocaleString('sk-SK') || '—'}</strong><span>potvrdených dní</span></div>
      </div>
      {legacy && engineered && (
        <div className="model-comparison">
          <span>Iba počasie — rovnaké testovacie dni</span>
          <div><b>Pôvodných {legacy.feature_count} atribútov</b><strong>{Number(legacy.roc_auc).toFixed(3)} AUC</strong></div>
          <div><b>Nových {engineered.feature_count} atribútov</b><strong>{Number(engineered.roc_auc).toFixed(3)} AUC</strong></div>
          <p>
            Rozdiel {engineered.auc_change_vs_legacy > 0 ? '+' : ''}{Number(engineered.auc_change_vs_legacy).toFixed(3)}.
            Časové atribúty teda zatiaľ menia vysvetlenie viac než presnosť. Produkčný
            ekologický model je zmrazený vopred; táto tabuľka ho už nesmie nahradiť
            kandidátom s náhodne o zlomok vyšším AUC.
          </p>
        </div>
      )}
      <div className="model-comparison">
        <span>Kontrolné baseline modely</span>
        <div><b>Iba sezóna</b><strong>{Number(model.baseline?.roc_auc || 0).toFixed(3)} AUC</strong></div>
        <div><b>Sezóna + správanie hlásiacich</b><strong>{Number(model.reporting_baseline?.roc_auc || 0).toFixed(3)} AUC</strong></div>
        <p>Reportingový baseline používa počet hlásení a deň v týždni; produkčný model dostáva len obsah hlásení (rástli / nerástli za 7 a 14 dní), deň v týždni nie.</p>
      </div>
      <div className="method-note">
        <Info size={18} />
        <p>
          Výsledok je sezónny historický percentil vhodnosti, nie šanca. Tréning obsahuje
          {` ${target?.positive_days || '—'} `}pozitívnych a {target?.sampled_background_days || '—'} vzorkovaných
          neoznačených dní. Explicitných negatív je iba {target?.explicit_secondary_validation?.negative_days || '—'}.
        </p>
      </div>
      {weatherAudit.status === 'available' && (
        <div className="method-note">
          <Gauge size={18} />
          <p>Kontrola zdrojov na {weatherAudit.overlap_days} dňoch: odchýlka dažďa {weatherAudit.rain_mae_mm} mm/deň, teploty {weatherAudit.temperature_mae_c} °C. Archív sa už neprepisuje živým endpointom.</p>
        </div>
      )}
      {spatial.audit && (
        <div className="spatial-model-audit">
          <span>Priestorový audit</span>
          <strong>{spatial.training_region_matched ? 'Malacky sú párované na úrovni regiónu' : 'Tréningové dáta zatiaľ nie sú párované podľa lokality'}</strong>
          <p>{spatial.audit}</p>
          <small>Mapa používa {spatial.guide_weather_units} samostatných lokálnych buniek počasia. Ďalší krok: georeferencované nálezy priradiť k rovnakým lesným zónam a model znovu validovať.</small>
        </div>
      )}
    </aside>
  )
}

function Loading() {
  return (
    <main className="state-screen" aria-live="polite">
      <div className="brand-mark"><Sprout /></div>
      <h1>Načítavam lesné signály…</h1>
      <div className="loading-line"><i /></div>
    </main>
  )
}

function ErrorState({ onRetry }) {
  return (
    <main className="state-screen">
      <div className="brand-mark brand-mark--error"><CloudRain /></div>
      <h1>Predpoveď sa nepodarilo načítať</h1>
      <p>API možno ešte pripravuje čerstvé dáta. Skús to znovu o chvíľu.</p>
      <button className="primary-button" onClick={onRetry}><RefreshCw size={17} /> Skúsiť znovu</button>
    </main>
  )
}

function getDashboard() {
  return fetch('/api/dashboard').then((response) => {
    if (!response.ok) throw new Error('Dashboard request failed')
    return response.json()
  })
}

export default function App() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)

  const retry = () => {
    setError(false)
    setData(null)
    getDashboard().then(setData)
      .catch(() => setError(true))
  }

  useEffect(() => {
    getDashboard().then(setData).catch(() => setError(true))
  }, [])

  if (error) return <ErrorState onRetry={retry} />
  if (!data) return <Loading />
  if (!data.forecast?.length) return <ErrorState onRetry={retry} />

  const [current, ...future] = data.forecast

  return (
    <div className="app-shell">
      <a className="skip-link" href="#obsah">Preskočiť na obsah</a>
      <header className="site-header">
        <a className="brand" href="#top" aria-label="Myko Záhorie domov">
          <span className="brand-mark"><Sprout /></span>
          <span><strong>MYKO</strong><small>ZÁHORIE</small></span>
        </a>
        <nav aria-label="Hlavná navigácia">
          <a href="#predpoved">Predpoveď</a>
          <a href="#historia">História</a>
          <a href="#tvar-modelu">Model</a>
          <a href="#druhy">Druhy</a>
          <a href="#spatny-test">Spätný test</a>
          <a href="#mapa">Mapa</a>
          <a href="#zrazky">Zrážky</a>
          <a href="#spravy">Správy</a>
          <a href="#metodika">O modeli</a>
        </nav>
        <div className="region-pill"><MapPin size={15} /> Záhorie</div>
      </header>

      <main id="obsah">
        <section className="hero" id="predpoved">
          <div className="forest-orb forest-orb--one" />
          <div className="forest-orb forest-orb--two" />
          <div className="hero-inner">
            <ForecastCard item={current} current curve={data.prediction_timeline} />
            <div className="forecast-stack">
              <div className="stack-heading">
                <span>Výhľad</span>
                <small>model + počasie</small>
              </div>
              {future.map((item) => <ForecastCard item={item} key={item.horizon_days} />)}
              <div className="uncertainty-note">
                <Info size={16} /> Čím ďalej do budúcnosti, tým širšia neistota počasia.
              </div>
            </div>
          </div>
        </section>

        <div className="content-wrap">
          <WeatherStrip item={current} />

          <DecisionSummary current={current} points={data.prediction_timeline} guide={data.guide_today} />

          <RegionMap stations={data.stations} military={data.military} guide={data.guide_today} forecastPoints={data.prediction_timeline} accessCalendar={data.access_calendar} />

          <PredictionTimeline points={data.prediction_timeline} asOf={data.meta.as_of} />

          <WeatherTimeSeries asOf={data.meta.as_of} />

          <RainTimeline
            context={data.rain_context}
            analysis={data.condition_analysis}
            asOf={data.meta.as_of}
          />

          <ModelShapePanel curves={data.model?.shape_functions} asOf={data.meta.as_of} />

          <SpeciesPanel />

          <BacktestPanel />

          <div className="two-column">
            <Stations stations={data.stations} />
            <MethodCard model={data.model} />
          </div>

          <NewsFeed news={data.news} />

          <section className="safety-callout">
            <div className="safety-icon"><Info /></div>
            <div>
              <h2>Predpoveď nie je atlas húb</h2>
              <p>Neznámu hubu nikdy nejedz podľa fotografie ani podľa tejto stránky. Určenie druhu over u skúseného mykológa.</p>
            </div>
          </section>
        </div>
      </main>

      <footer>
        <div className="brand brand--footer">
          <span className="brand-mark"><Sprout /></span>
          <span><strong>MYKO</strong><small>ZÁHORIE</small></span>
        </div>
        <p>Dáta: SHMÚ, Open-Meteo, Meteostat, iNaturalist, MO SR a lokálny export nahuby.sk.</p>
        <p>Denne obnovované · posledný beh {formatDateTime(data.meta.generated_at)} · stav k {formatDate(data.meta.as_of, { year: 'numeric' })}</p>
      </footer>
    </div>
  )
}
